import { fetchBaseQuery, createApi } from '@reduxjs/toolkit/query/react';
import { getAppConfig } from '@/app/config';
import type { LngLatBase } from '@/views/map/types';
import type {
  MapboxRetreiveResponse,
  MapboxSuggestionResponse,
} from './mapboxTypes';

interface MapboxSuggestionRaw {
  value: string;
  subtitle: string;
  mapboxId: string;
}

// https://docs.mapbox.com/api/search/search-box/#search-box-api-pricing
const sessionToken = crypto.randomUUID();

export const mapboxApi = createApi({
  reducerPath: 'mapboxApi',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://api.mapbox.com/search/searchbox/v1/',
  }),
  endpoints: (builder) => ({
    suggest: builder.query({
      query: ({
        addressQuery,
        coords,
        bbox,
        limit = 10,
      }: {
        addressQuery: string;
        coords: LngLatBase;
        bbox?: number[];
        limit?: number;
      }) => {
        const { mapboxToken } = getAppConfig();

        if (!mapboxToken) {
          throw new Error('Mapbox token not found in environment variables.');
        }

        /** The default API seemed to return Russian addresses more often than
         * made sense, so we're limiting the search to the following countries.
         */
        const ALLOWED_COUNTRIES = [
          // Western Hemisphere (North America, Central America, South America, Caribbean)
          'AG',
          'AI',
          'AR',
          'AW',
          'BB',
          'BM',
          'BO',
          'BS',
          'BZ',
          'CA',
          'CL',
          'CO',
          'CR',
          'CU',
          'CW',
          'DM',
          'DO',
          'EC',
          'SV',
          'GD',
          'GT',
          'GY',
          'HN',
          'HT',
          'JM',
          'KY',
          'KN',
          'LC',
          'MF',
          'MX',
          'NI',
          'PA',
          'PE',
          'PR',
          'PY',
          'SR',
          'SX',
          'TT',
          'TC',
          'US',
          'UY',
          'VC',
          'VE',
          'VG',
          'VI',

          // Europe
          'AD',
          'AL',
          'AT',
          'BA',
          'BE',
          'BG',
          'BY',
          'CH',
          'CY',
          'CZ',
          'DE',
          'DK',
          'EE',
          'ES',
          'FI',
          'FR',
          'GB',
          'GR',
          'HR',
          'HU',
          'IE',
          'IS',
          'IT',
          'LI',
          'LT',
          'LU',
          'LV',
          'MC',
          'MD',
          'ME',
          'MK',
          'MT',
          'NL',
          'NO',
          'PL',
          'PT',
          'RO',
          'RS',
          'SE',
          'SI',
          'SK',
          'SM',
          'UA',
          'VA',

          // Australia & New Zealand
          'AU',
          'NZ',

          // Japan
          'JP',
        ];

        const params = new URLSearchParams({
          q: addressQuery,
          proximity: `${coords.lng},${coords.lat}`,
          access_token: mapboxToken,
          session_token: sessionToken,
          country: ALLOWED_COUNTRIES.join(','), // Limit to allowed countries
          limit: limit.toString(),
          types: 'address,postcode,street,city',
          ...(bbox?.length ? { bbox: bbox.join(',') } : {}),
        });

        return `suggest?${params.toString()}`;
      },

      transformResponse: (response: MapboxSuggestionResponse) => {
        return response.suggestions.map((suggestion) => {
          const suggest: MapboxSuggestionRaw = {
            value: '',
            subtitle: '',
            mapboxId: suggestion.mapbox_id,
          };

          switch (suggestion.feature_type) {
            case 'address':
              suggest.value = suggestion.name;
              suggest.subtitle = `${
                suggestion.context.place?.name ?? 'Unknown'
              }, ${suggestion.context.region?.name ?? 'Unknown'}`;
              break;
            case 'postcode':
              suggest.value = suggestion.name;
              suggest.subtitle = `${
                suggestion.context.district?.name ??
                suggestion.context.place?.name ??
                'Unknown'
              }, ${suggestion.context.region?.name ?? 'Unknown'}`;
              break;
            case 'street':
              suggest.value = suggestion.context.street?.name ?? 'Unknown';
              suggest.subtitle = `${
                suggestion.context.place?.name ?? 'Unknown'
              }, ${suggestion.context.region?.name ?? 'Unknown'}`;
              break;
            case 'city':
              suggest.value = suggestion.context.locality?.name ?? 'Unknown';
              suggest.subtitle = '(city)';
              break;
            case 'place':
              // The API _shouldn't_ be returning places, but for some reason, it is. Let's handle it.
              suggest.value = suggestion.name;
              suggest.subtitle = `${
                suggestion.context.region?.name ?? 'Unknown'
              }`;
              break;
            default:
              break;
          }

          return suggest;
        });
      },
    }),

    retrieve: builder.query({
      query({ mapboxId }) {
        const { mapboxToken } = getAppConfig();

        if (!mapboxToken) {
          throw new Error('Mapbox token not found in environment variables.');
        }

        const params = new URLSearchParams({
          access_token: mapboxToken,
          session_token: sessionToken,
        });

        return `retrieve/${mapboxId}?${params.toString()}`;
      },
      transformResponse: (response: MapboxRetreiveResponse) => {
        return response.features.map((feature) => {
          return {
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0],
          } satisfies LngLatBase;
        })[0]; // return the first coordinates, there should be only one result.
      },
    }),
  }),
});

export const { useLazySuggestQuery, useLazyRetrieveQuery } = mapboxApi;
